import React from 'react';
import Constants from '../../submodules/logictry_config/constants';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import UserAccount, { ACCOUNT_DELETED_POPUP, GET_APP_POPUP, LOGIN_POPUP, REGISTER_POPUP, RESET_PASSWORD_POPUP } from '../../services/UserAccount';
import Navigation from '../../services/Navigation';
import Storage from '../../services/Storage';
import WindowSize from '../../services/WindowSize';
import Server from '../../services/server';

// Shell components
import Popup from '../Popup';

// Regex helpers
import {
  isFullnameInvalid,
  isUsernameInvalid,
  isEmailInvalid,
} from '../../utils/regex';
import { Card, Form, Input, InputWrapper, LogoHeader, Subtitle, Title, WarningText, PrimaryButton, SecondaryTextButton, SmallHint } from '../../styledhtml/Tailwind';
import ChangePassword from './ChangePassword';

// ---------------------------------------------------------
// Set Username Component
// ---------------------------------------------------------
class ChangeUsername extends LogictryComponent {
  state = {
    username: ''
  }

  submitPressed = () => {
    const { username } = this.state;
    const { account } = UserAccount;
    if (!username) return this.setState({ warning: 'Username blank' });
    if (username && isUsernameInvalid(username)) return this.setState({ warning: 'Invalid username' });
    Server.updateProfile({ _id: account._id, username }).then((r) => {
      if (!r) return this.setState({ warning: 'Something went wrong' });
      if (r.error === 'InvalidUsername') return this.setState({ warning: 'Invalid username' });
      if (r.error === 'UsernameAlreadyRegistered') return this.setState({ warning: 'Username is already in use' });
      UserAccount.getUserProfile(r);
      return this.setState({ warning: '' });
    });
  };

  render() {
    if (UserAccount.account.username) return null;
    if (Storage.state.skipUsername && ((new Date() - new Date(Storage.state.skipUsername)) / 1000) < 2592000) return null;
    const { username, warning } = this.state;
    return (
      <Popup>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>Claim Your Username</Title>
          <Subtitle>Choose it now before someone else takes it.</Subtitle>

          <Form>
            <InputWrapper>
              <Input
                placeholder={'Username'}
                type="text"
                value={username}
                onChange={(e) => this.setState({ username: e.target.value.replaceAll(' ', '') })}
                autoFocus={!WindowSize.mobile}
              />
            </InputWrapper>

            <PrimaryButton onClick={this.submitPressed}>
              {'Claim Username'}
            </PrimaryButton>

            <WarningText>{warning}</WarningText>

            <SecondaryTextButton onClick={() => {
              Storage.setSkipUsername();
              this.forceUpdate();
            }}>
              {`Continue without a username`}
            </SecondaryTextButton>
          </Form>
        </Card>
      </Popup>
    );
  }
}

// ---------------------------------------------------------
// VerificationCode Component
// ---------------------------------------------------------
class VerificationCode extends LogictryComponent {
  constructor(props) {
    super(props);
    this.state = {
      warning: '',
      verificationCode: '',
      lastVerificationCodeSent: '',
    };
  }

  verifyCode = () => {
    const { verificationCode, lastVerificationCodeSent } = this.state;

    if (!verificationCode)
      return this.setState({ warning: 'Code missing' });

    if (verificationCode === lastVerificationCodeSent)
      return this.setState({ warning: 'Code invalid' });

    this.setState({ warning: '' });

    UserAccount.verifyCode(UserAccount.account.email, verificationCode)
      .then((result) => {
        this.setState({ lastVerificationCodeSent: verificationCode });

        if (result?._id) {
          this.props.onSuccess && this.props.onSuccess();
        } else {
          this.setState({
            warning: 'Code invalid — a new one-time use code has been sent!',
          });
        }
      });
  };

  render() {
    const { verificationCode, warning } = this.state;

    return (
      <>
        <Title>Enter the code</Title>
        <Subtitle>We sent a one-time code to your email.</Subtitle>

        <Form>
          <InputWrapper>
            <Input
              placeholder="Verification Code"
              value={verificationCode}
              maxLength={30}
              onChange={(e) =>
                this.setState({
                  verificationCode: e.target.value.replaceAll(' ', ''),
                  lastVerificationCodeSent: '',
                })
              }
              onKeyDown={(e) => e.key === 'Enter' && this.verifyCode()}
              autoFocus={!WindowSize.mobile}
            />
          </InputWrapper>

          <PrimaryButton onClick={this.verifyCode}>Submit</PrimaryButton>
          <WarningText>{warning}</WarningText>
        </Form>
      </>
    );
  }
}

// ---------------------------------------------------------
// ResetPasswordPopup Component
// ---------------------------------------------------------
class ResetPasswordPopup extends LogictryComponent {
  constructor(props) {
    super(props);
    this.state = {
      email: UserAccount.account.email || '',
      warning: '',
      state: '',
    };
  }

  resetPressed = () => {
    const { email } = this.state;

    if (!email) return this.setState({ warning: 'Email missing' });

    this.setState({ state: 'resetting', warning: '' });

    UserAccount.resetPassword(email).then(({ success }) => {
      UserAccount.account.email = email;
      UserAccount.account.error = success
        ? 'VerificationCodeRequired'
        : 'VerificationCodeInvalid';
      UserAccount.showRegisterPopup();
    });
  };

  render() {
    const { email, warning } = this.state;

    return (
      <>
        <Title>Reset Password</Title>
        <Subtitle>Enter your email to receive a reset code.</Subtitle>

        <Form>
          <InputWrapper>
            <Input
              placeholder="Email"
              inputMode="email"
              value={email}
              maxLength={256}
              onChange={(e) => this.setState({ email: e.target.value.replaceAll(' ', '') })}
              onKeyDown={(e) => e.key === 'Enter' && this.resetPressed()}
              autoFocus={!WindowSize.mobile}
            />
          </InputWrapper>

          <PrimaryButton onClick={this.resetPressed}>
            Reset Password
          </PrimaryButton>

          <WarningText>{warning}</WarningText>

          <SecondaryTextButton onClick={() => UserAccount.showLoginPopup()}>
            Already have an account?
          </SecondaryTextButton>

          <SecondaryTextButton onClick={() => UserAccount.showRegisterPopup()}>
            Don’t have an account?
          </SecondaryTextButton>

          {Constants.isLogicWiki && <SmallHint>
            New to Logictry?{' '}
            <span
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => Navigation.push(Navigation.logicCircle)}
            >
              Learn how it works.
            </span>
          </SmallHint>}
        </Form>
      </>
    );
  }
}

// ---------------------------------------------------------
// GetAppPopup Component
// ---------------------------------------------------------
class GetAppPopup extends LogictryComponent {
  constructor(props) {
    super(props);
    this.state = {
      email: UserAccount.account.email || '',
      warning: '',
    };
  }

  sendLink = () => {
    const { email } = this.state;

    if (!email) return this.setState({ warning: 'Email missing' });

    this.setState({ state: 'resetting', warning: '' });

    Server.getApp({ email });

    UserAccount.clearPopup();
  };

  render() {
    const { email, warning } = this.state;

    return (
      <>
        <Title>Download the app</Title>
        <Subtitle>Scan the QR code or enter your email.</Subtitle>
        <img
            style={{ maxWidth: '14rem', margin: 'auto', display: 'block' }}
            src="https://cdn.logictry.com/eyJidWNrZXQiOiJsb2dpY3RyeXN0YXRpYyIsImtleSI6ImltYWdlcy9nZXRhcHBxcmNvZGUuc3ZnIiwiZWRpdHMiOnt9fQ=="
          ></img>
        <Form>
          <InputWrapper>
            <Input
              placeholder="Email"
              inputMode="email"
              value={email}
              maxLength={256}
              onChange={(e) => this.setState({ email: e.target.value.replaceAll(' ', '') })}
              onKeyDown={(e) => e.key === 'Enter' && this.sendLink()}
              autoFocus={!WindowSize.mobile}
            />
          </InputWrapper>

          <PrimaryButton onClick={this.sendLink}>
            Send Link
          </PrimaryButton>

          {warning && <WarningText>{warning}</WarningText>}
        </Form>
      </>
    );
  }
}

class AccountDeletedPopup extends LogictryComponent {
  constructor(props) {
    super(props);
    this.state = {
      email: UserAccount.account.email || '',
      warning: '',
    };
    UserAccount.account.error = '';
    UserAccount.account.email = '';
  }

  render() {
    const { email } = this.state;

    return (
      <>
        <Title>Account Permanently Deleted</Title>
        <Subtitle>To restore this account, please email team@logictry.com from <b>{email}</b> requesting the account to be restored.</Subtitle>
      </>
    );
  }
}

// ---------------------------------------------------------
// RegisterPage Component
// ---------------------------------------------------------
class RegisterPage extends LogictryComponent {
  constructor(props) {
    super(props);

    const params = Navigation.currentSearch;

    this.state = {
      verificationCodeRequired:
        UserAccount.account.error === 'VerificationCodeRequired',
      email: params.email || UserAccount.account.email || '',
      fullname: UserAccount.account.fullname || '',
      warning: '',
      registeredScreen: '',
    };
  }

  componentDidMount() {
    UserAccount.onStateUpdate(this.accountUpdate);
  }

  componentWillUnmount() {
    if (!UserAccount.isLoggedIn) UserAccount.account.error = '';
    UserAccount.offStateUpdate(this.accountUpdate);
  }

  accountUpdate = () => this.forceUpdate();

  nextPressed = () => {
    const { fullname, email } = this.state;

    if (!fullname) return this.setState({ warning: 'Full Name blank' });
    if (isFullnameInvalid(fullname)) return this.setState({ warning: 'Invalid fullname' });
    if (!email) return this.setState({ warning: 'Email blank' });
    if (isEmailInvalid(email)) return this.setState({ warning: 'Invalid email' });

    this.setState({ warning: '' });
    UserAccount.registerAccount({
      fullname,
      email,
      reply: 'Welcome',
    }).then((r) => {
      if (!r) return this.forceUpdate();

      UserAccount.account.email = email;

      if (r.error === 'AccountAlreadyExists') return UserAccount.showLoginPopup();
      if (r.error === 'EmailAlreadyRegistered') return this.setState({ warning: 'Email already registered' });
      if (r.error) return this.setState({ warning: 'Something went wrong' });

      this.setState({
        warning: 'Success!',
        verificationCodeRequired: true,
      });
    });
  };

  verificationCodeSuccess = () =>
    this.setState({
      verificationCodeRequired: false,
      registeredScreen: 'ChangePassword',
    });

  getRegisterContent = () => {
    const { fullname, email, warning } = this.state;

    return (
      <>
        <Title>Create your account</Title>

        <Form>
          <InputWrapper>
            <Input
              placeholder="Full Name"
              value={fullname}
              maxLength={50}
              onChange={(e) => this.setState({ fullname: e.target.value })}
              autoFocus={!WindowSize.mobile}
            />
          </InputWrapper>

          <InputWrapper>
            <Input
              placeholder="Email"
              inputMode="email"
              value={email}
              maxLength={256}
              onChange={(e) => this.setState({ email: e.target.value.replaceAll(' ', '') })}
              onKeyDown={(e) => e.key === 'Enter' && this.nextPressed()}
            />
          </InputWrapper>

          <PrimaryButton onClick={this.nextPressed}>
            Sign Up
          </PrimaryButton>

          <WarningText>{warning}</WarningText>

          <SecondaryTextButton onClick={() => UserAccount.showLoginPopup()}>
            Already have an account?
          </SecondaryTextButton>

          {Constants.isLogicWiki && <SmallHint>
            New to Logictry?{' '}
            <span
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => Navigation.push(Navigation.logicCircle)}
            >
              Learn how it works.
            </span>
          </SmallHint>}
        </Form>
      </>
    );
  };

  getRegisteredContent = () => (
    <ChangePassword email={this.state.email} />
  );

  render() {
    const { verificationCodeRequired, registeredScreen } = this.state;

    if (verificationCodeRequired) return <VerificationCode onSuccess={this.verificationCodeSuccess} />;

    if (registeredScreen) return this.getRegisteredContent();

    return this.getRegisterContent();
  }
}

// ---------------------------------------------------------
// LoginPage Component
// ---------------------------------------------------------
class LoginPage extends LogictryComponent {
  constructor() {
    super();
    UserAccount.account.token = '';
    this.state = {
      page: UserAccount.account.password ? 'PASSWORD' : '',
      warning: '',
    };
  }

  componentDidMount() {
    UserAccount.onStateUpdate(this.accountUpdate);
  }

  componentWillUnmount() {
    UserAccount.offStateUpdate(this.accountUpdate);
  }

  accountUpdate = () => {
    const { page } = this.state;

    if (UserAccount.account.error === 'IncorrectPassword') {
      if (page === 'PASSWORD' && UserAccount.account.password && UserAccount.isLoggedOut) {
        this.setState({ warning: 'Password incorrect' });
        UserAccount.account.password = '';
      }
      return this.setState({ page: 'PASSWORD' });
    }

    if (UserAccount.account.error === 'UserPasswordMissing') {
      return setTimeout(() => UserAccount.showRegisterPopup(), 250);
    }

    if (UserAccount.account.error === 'MFAVerificationFailed') {
      return this.setState({
        warning: 'Invalid Code',
        page: 'MFA',
      });
    }

    if (UserAccount.account.error === 'MFAVerificationRequired') {
      return this.setState({ page: 'MFA' });
    }

    if (UserAccount.isLoggedIn) {
      this.setState({ warning: '' })
      UserAccount.clearPopup();
    }

    this.forceUpdate();
  };

  login = () => {
    const { page } = this.state;

    if (page === 'MFA' && !UserAccount.account?.token)
      return this.setState({ warning: 'Code missing' });

    if (!UserAccount.account?.email)
      return this.setState({ warning: 'Email missing' });

    this.setState({ warning: '' });

    UserAccount.loginAccount({
      email: UserAccount.account.email,
      password: UserAccount.account.password,
      token: UserAccount.account.token,
    });
  };

  render() {
    const { page, warning } = this.state;

    if (UserAccount.account.error === 'UserNotFound') {
      UserAccount.account.error = '';
      UserAccount.showRegisterPopup();
    }
    if (UserAccount.account.error === 'VerificationCodeRequired') {
      UserAccount.showRegisterPopup();
    }
    // MFA flow
    // if (page === 'MFA') {
    //   return (
    //     <>
    //       <Title>Multi-Factor Authentication</Title>
    //       <Subtitle>Enter your verification code.</Subtitle>

    //       <Form>
    //         <InputWrapper>
    //           <Input
    //             placeholder="Verification Code"
    //             value={UserAccount.account.token || ''}
    //             onChange={(e) => {
    //               UserAccount.account.token = e.target.value;
    //               this.forceUpdate();
    //             }}
    //             onKeyDown={(e) => e.key === 'Enter' && this.login()}
    //             autoFocus={!WindowSize.mobile}
    //           />
    //         </InputWrapper>

    //         <PrimaryButton onClick={this.login}>Login</PrimaryButton>
    //         <WarningText>{warning}</WarningText>
    //       </Form>
    //     </>
    //   );
    // }

    return (
      <>
        <Title>Login to your account</Title>

        <Form>
          <InputWrapper>
            <Input
              placeholder="Email or Username"
              inputMode="email"
              value={UserAccount.account.email || ''}
              onChange={(e) => {
                UserAccount.account.email = e.target.value.replaceAll(' ', '');
                this.forceUpdate();
              }}
              onKeyDown={(e) => e.key === 'Enter' && this.login()}
              autoFocus={!WindowSize.mobile && page !== 'PASSWORD'}
            />
          </InputWrapper>

          {page === 'PASSWORD' && (
            <InputWrapper>
              <Input
                placeholder="Password"
                type="password"
                value={UserAccount.account.password || ''}
                onChange={(e) => {
                  UserAccount.account.password = e.target.value.replaceAll(' ', '');
                  this.forceUpdate();
                }}
                onKeyDown={(e) => e.key === 'Enter' && this.login()}
                autoFocus={!WindowSize.mobile && page === 'PASSWORD'}
              />
            </InputWrapper>
          )}

          <PrimaryButton onClick={this.login}>Submit</PrimaryButton>
          <WarningText>{warning}</WarningText>

          <SecondaryTextButton onClick={() => UserAccount.showRegisterPopup()}>
            Don’t have an account?
          </SecondaryTextButton>

          <SecondaryTextButton onClick={() => UserAccount.showResetPasswordPopup()}>
            Forgot password?
          </SecondaryTextButton>

          {Constants.isLogicWiki && <SmallHint>
            New to Logictry?{' '}
            <span
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => Navigation.push(Navigation.logicCircle)}
            >
              Learn how it works.
            </span>
          </SmallHint>}
        </Form>
      </>
    );
  }
}

// ---------------------------------------------------------
// AuthPopup Root Component
// ---------------------------------------------------------
export default class AuthPopup extends LogictryComponent {
  componentDidMount() {
    UserAccount.onStateUpdate(this);
    Navigation.onStateUpdate(this);
  }

  componentWillUnmount() {
    UserAccount.offStateUpdate(this);
    Navigation.offStateUpdate(this);
  }

  render() {
    const { showPopup, isLoggedIn } = UserAccount;
    const path = Navigation.currentLocation[1];

    if (isLoggedIn && !path) return <ChangeUsername />;

    if (!showPopup || isLoggedIn) return null;
    return (
      <Popup
        showCloseButton
        allowBackgroundClose
        onClose={() => UserAccount.clearPopup()}
      >
        <Card onClick={(e) => e.stopPropagation()}>
          <LogoHeader>
            <img src={Constants.Logo} alt="Logictry" />
          </LogoHeader>

          {showPopup === LOGIN_POPUP && <LoginPage />}
          {showPopup === REGISTER_POPUP && <RegisterPage />}
          {showPopup === RESET_PASSWORD_POPUP && <ResetPasswordPopup />}
          {showPopup === GET_APP_POPUP && <GetAppPopup />}
          {showPopup === ACCOUNT_DELETED_POPUP && <AccountDeletedPopup />}
        </Card>
      </Popup>
    );
  }
}
